import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../config";
import { useAuth } from '../context/AuthContext';

const PaymentModal = ({ onClose }) => {
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [loading, setLoading] = useState(false);
    const { user } = useAuth();

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const response = await axios.get(`${config.API_BASE_URL}/subscriptions/subscription-plans/`);
                setSubscriptionPlans(response.data?.plans || response.data || []);
            } catch (error) {
                // console.error("Error fetching subscription plans:", error);
                setSubscriptionPlans([]);
            }
        };

        fetchPlans();
    }, []);

    const validatePhoneNumber = (number) => {
        return /^\d{10,15}$/.test(number); // Allows 10-15 digit numbers only
    };

    const handlePayment = async () => {
        if (!selectedPlan || !phoneNumber) {
            alert("Please enter a valid phone number and select a plan.");
            return;
        }

        if (!validatePhoneNumber(phoneNumber)) {
            alert("Invalid phone number. It should be 10-15 digits.");
            return;
        }

        const selectedPlanDetails = subscriptionPlans.find(plan => plan.id === Number(selectedPlan));
        if (!selectedPlanDetails) {
            alert("Invalid plan selected.");
            return;
        }

        setLoading(true);
        const amount = selectedPlanDetails.price;
        const userId = user?.id;

        try {
            const response = await axios.post(`${config.API_BASE_URL}/payments/process-payment/`, {
                phoneNumber,
                amount,
                userId,
                selectedPlan,
            });

            const checkoutRequestId = response.data?.CheckoutRequestID;
            if (!checkoutRequestId) {
                throw new Error("Checkout request ID is missing!");
            }

            let attempts = 0; // Track polling attempts
            const maxAttempts = 5; // Stop checking after 5 attempts

            const checkPaymentStatus = async () => {
                try {
                    const statusResponse = await axios.get(`${config.API_BASE_URL}/payments/status/${checkoutRequestId}`);
                    // console.log("Payment status response:", statusResponse.data); // Log response for debugging

                    if (statusResponse.data.status === 'PAID') {
                        // alert("Payment successful!");
                        clearInterval(polling); // Stop polling
                        window.location.reload(); // Reload page
                    } else if (statusResponse.data.status === 'FAILED') {
                        alert("Payment failed, please try again.");
                        clearInterval(polling); // Stop polling
                    } else {
                        attempts++;
                        if (attempts >= maxAttempts) {
                            clearInterval(polling); // Stop polling after max attempts
                            alert("Payment is still pending. Please retry the payment or contact support.");
                        }
                    }
                } catch (error) {
                    // console.error("Error checking payment status:", error);
                    clearInterval(polling);
                    alert("An error occurred while checking payment status. Please try again.");
                }
            };

            // Check payment status every 5 seconds
            const polling = setInterval(checkPaymentStatus, 5000);

        } catch (error) {
            // console.error("Payment error:", error.response?.data || error);
            alert(`Payment error: ${error.response?.data?.message || "Check console for details"}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-md transform transition-all duration-300">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Renew Subscription (M-Pesa)</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-500 p-2"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="space-y-6">
                    <div className="space-y-2">
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone Number</label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                            </div>
                            <input
                                type="tel"
                                id="phone"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                                placeholder="Enter phone number"
                                className={`w-full px-10 py-3 rounded-lg border ${phoneNumber && !validatePhoneNumber(phoneNumber) ? 'border-red-500' : 'border-gray-300'
                                    } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm placeholder-gray-400 transition-all duration-200`}
                            />
                            {!validatePhoneNumber(phoneNumber) && phoneNumber && (
                                <p className="text-red-500 text-xs mt-1">Phone number must be 10-15 digits</p>
                            )}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="plan" className="block text-sm font-medium text-gray-700">Choose Subscription Plan</label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                </svg>
                            </div>
                            <select
                                id="plan"
                                value={selectedPlan}
                                onChange={(e) => setSelectedPlan(e.target.value)}
                                className="w-full px-10 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            >
                                <option value="" disabled>Select a Plan</option>
                                {subscriptionPlans.length > 0 ? (
                                    subscriptionPlans.map((plan) => (
                                        <option key={plan.id} value={plan.id}>
                                            {plan.name} - ${plan.price}
                                        </option>
                                    ))
                                ) : (
                                    <option disabled>Loading plans...</option>
                                )}
                            </select>
                        </div>
                    </div>

                    <button
                        onClick={handlePayment}
                        disabled={loading || !selectedPlan || !validatePhoneNumber(phoneNumber)}
                        className={`w-full px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center ${loading || !selectedPlan || !validatePhoneNumber(phoneNumber)
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700'
                            } text-white`}
                    >
                        {loading ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Processing...
                            </>
                        ) : (
                            'Proceed to Payment'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PaymentModal;
